/**
 * Small string helpers shared by the scorer and the split engine.
 * Dependency-free on purpose, like everything else in core.
 */

export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previous: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current: number[] = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = (previous[j - 1] ?? 0) + (a[i - 1] === b[j - 1] ? 0 : 1);
      const insertion = (current[j - 1] ?? 0) + 1;
      const deletion = (previous[j] ?? 0) + 1;
      current[j] = Math.min(substitution, insertion, deletion);
    }
    previous = current;
  }
  return previous[b.length] ?? 0;
}

/** Lowercase, strip diacritics and everything that is not a letter or digit. */
export const normaliseName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');

/** 0..1 name similarity. "Grilled Ribs" vs "Griled Rlbs" should still match. */
export function nameSimilarity(a: string, b: string): number {
  const left = normaliseName(a);
  const right = normaliseName(b);
  if (left === '' && right === '') return 1;
  const longest = Math.max(left.length, right.length);
  if (longest === 0) return 1;
  return 1 - editDistance(left, right) / longest;
}

/** Words of a sentence, normalised the same way names are. */
export const words = (sentence: string): string[] =>
  sentence
    .split(/[^\p{L}\p{N}]+/u)
    .map(normaliseName)
    .filter((word) => word.length > 0);
